import { skipToken, useSuspenseQuery } from "@apollo/client/react";
import { parseISO } from "date-fns";
import { ChevronsRight, Pencil } from "lucide-react";
import { type ReactNode, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  GetRecordComposerDefinitionDetailDocument,
  type GetRecordComposerDefinitionDetailQuery,
  GetRecordComposerDefinitionsDocument,
} from "~/common/gqls";
import { DetailLoading } from "~/components/loading";
import { Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui";
import { useDipLifecycle } from "~/features/dataImportProjects/context";
import { useDipResourceMutationGuard } from "~/features/dataImportProjects/hooks/useDipResourceMutationGuard";
import { useEntityComposerActivation } from "~/features/dataImportProjects/hooks/useEntityComposerActivation";
import { useRelationComposerActivation } from "~/features/dataImportProjects/hooks/useRelationComposerActivation";
import {
  zEntityComposerDefinition,
  zRelationComposerDefinition,
} from "~/features/dataImportProjects/utils/composerDefinition";
import { useTenant } from "~/features/tenant/context";
import { v2Route } from "~/v2/routes";
import { EntityComposerEditForm, RelationComposerEditForm } from "./ComposerEditForm";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";

type ComposerDef = GetRecordComposerDefinitionDetailQuery["recordComposerDefinitions"][number];
type EvidenceSetRef = ComposerDef["recordComposerDefinitionEvidenceSets"][number]["evidenceSet"];
type Mapping = { source: string; type?: string; target: string };

function formatDate(t: ReturnType<typeof useTranslation>["t"], iso: string | null | undefined) {
  return iso ? t("date.short", { date: parseISO(iso) }) : "-";
}

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-3 border-t border-border-default py-2 first:border-t-0">
      <dt className="text-12-regular text-fg-3">{label}</dt>
      <dd className="min-w-0 break-words text-14-regular text-fg-1">{children}</dd>
    </div>
  );
}

function JsonValue({ value }: { value: unknown }) {
  return <code className="break-all text-12-regular text-fg-1">{JSON.stringify(value ?? {})}</code>;
}

function EvidenceSetLinks({ dipId, evidenceSets }: { dipId: string; evidenceSets: EvidenceSetRef[] }) {
  const { t } = useTranslation();
  if (evidenceSets.length === 0) {
    return <span className="text-fg-3">-</span>;
  }
  return (
    <ul className="space-y-1">
      {evidenceSets.map((es) => (
        <li key={es.id} className="flex items-center gap-1.5">
          {es.deactivatedAt ? <Badge tone="status-draft">{t("ui.deactivated")}</Badge> : null}
          <Link
            to={v2Route.dataImportProjects.detail.evidenceSet.$buildPath({
              params: { id: dipId, evidenceSetId: es.id },
            })}
            className="text-14-regular text-primary-700 hover:underline"
          >
            {es.name || `(${t("ui.noName")})`}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function MappingsTable({ rows, showType }: { rows: Mapping[]; showType: boolean }) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return null;
  }
  return (
    <section className="space-y-2">
      <h3 className="text-14-bold capitalize text-fg-1">{t("ui.mappings")}</h3>
      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
        <table className="w-full min-w-[560px] border-collapse text-12-regular">
          <thead className="bg-bg-subtle text-fg-2">
            <tr>
              <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                {t("model.mappingSet.sourcePropertyName")}
              </th>
              <th className="w-8 border-b border-border-default" />
              {showType ? (
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.type")}</th>
              ) : null}
              <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                {t("model.mappingSet.targetPropertyName")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={`${row.target}-${idx}`} className="border-t border-border-default first:border-t-0">
                <td className="px-3 py-2 align-top">
                  <code className="whitespace-pre-wrap break-all text-fg-1">{row.source}</code>
                </td>
                <td className="px-1 py-2 text-center text-fg-3">
                  <ChevronsRight className="inline size-4" />
                </td>
                {showType ? <td className="px-3 py-2 align-top text-fg-2">{row.type}</td> : null}
                <td className="px-3 py-2 align-top font-mono text-fg-1">{row.target}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ComposerDetailLayout({
  composerDef,
  dipId,
  onEdit,
  onActivate,
  onDeactivate,
  definitionRows,
  rawDefinition,
  mappings,
  showMappingType,
  editDialog,
  readOnly,
}: {
  composerDef: ComposerDef;
  dipId: string;
  onEdit: () => void;
  onActivate: () => void;
  onDeactivate: () => void;
  definitionRows: ReactNode;
  rawDefinition: unknown | null;
  mappings: Mapping[];
  showMappingType: boolean;
  editDialog: ReactNode;
  readOnly: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[18px] font-semibold text-fg-1">{composerDef.name}</h2>
              {composerDef.deactivatedAt ? <Badge tone="status-draft">{t("ui.deactivated")}</Badge> : null}
            </div>
            <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-12-regular text-fg-3">
              <div className="flex gap-1.5">
                <dt>{t("model.common.id")}:</dt>
                <dd className="font-mono">{composerDef.id}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.createdAt")}:</dt>
                <dd>{formatDate(t, composerDef.createdAt)}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.updatedAt")}:</dt>
                <dd>{formatDate(t, composerDef.updatedAt)}</dd>
              </div>
            </dl>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <DipReadOnlyTooltip disabled={readOnly} ariaLabel={t("ui.edit")}>
              <Button type="button" variant="outline" size="sm" disabled={readOnly} onClick={onEdit}>
                <Pencil className="size-3.5" />
                {t("ui.edit")}
              </Button>
            </DipReadOnlyTooltip>
            {composerDef.deactivatedAt ? (
              <DipReadOnlyTooltip disabled={readOnly} ariaLabel={t("ui.activate")}>
                <Button type="button" variant="ghost" size="sm" disabled={readOnly} onClick={onActivate}>
                  {t("ui.activate")}
                </Button>
              </DipReadOnlyTooltip>
            ) : (
              <DipReadOnlyTooltip disabled={readOnly} ariaLabel={t("ui.deactivate")}>
                <Button type="button" variant="ghost" size="sm" disabled={readOnly} onClick={onDeactivate}>
                  {t("ui.deactivate")}
                </Button>
              </DipReadOnlyTooltip>
            )}
          </div>
        </div>
      </section>

      <dl>
        <MetaRow label={t("model.evidenceSet.modelNames")}>
          <EvidenceSetLinks
            dipId={dipId}
            evidenceSets={composerDef.recordComposerDefinitionEvidenceSets.map((r) => r.evidenceSet)}
          />
        </MetaRow>
        {rawDefinition !== null ? (
          <MetaRow label={t("model.common.definition")}>
            <pre className="max-h-[320px] overflow-auto whitespace-pre-wrap break-all rounded-[var(--radius-md)] border border-border-default bg-bg-subtle p-3 text-12-regular">
              {JSON.stringify(rawDefinition, null, 2)}
            </pre>
          </MetaRow>
        ) : (
          definitionRows
        )}
      </dl>

      {mappings.length > 0 ? <MappingsTable rows={mappings} showType={showMappingType} /> : null}

      {editDialog}
    </div>
  );
}

function ComposerEditDialog({
  open,
  onOpenChange,
  title,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Suspense fallback={<DetailLoading />}>{children}</Suspense>
      </DialogContent>
    </Dialog>
  );
}

export function EntityComposerDetailPanel({ id }: { id: string }) {
  const { t } = useTranslation();
  const { isReadOnly, projectId } = useDipLifecycle();
  const { canExecuteMutation, setAuthorized } = useDipResourceMutationGuard(`entity-composer:${id}`);
  const { tenantId } = useTenant();
  const [openEditForm, setOpenEditForm] = useState(false);

  useEffect(() => {
    if (isReadOnly) setOpenEditForm(false);
  }, [isReadOnly]);

  const { data, dataState } = useSuspenseQuery(
    GetRecordComposerDefinitionDetailDocument,
    tenantId ? { variables: { tenantId, id } } : skipToken,
  );

  const composerDef = tenantId && dataState === "complete" ? data?.recordComposerDefinitions[0] : undefined;
  const dipId = composerDef?.dataImportProjectRecordComposerDefinition?.dataImportProjectId;
  const isAuthorized = dipId === projectId;
  setAuthorized(isAuthorized);
  const { onActivate, onDeactivate } = useEntityComposerActivation({
    listVariables: dipId ? { dataImportProjectId: dipId } : undefined,
    canExecute: canExecuteMutation,
  });

  if (!composerDef || !dipId || !isAuthorized) {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  const parsed = zEntityComposerDefinition.safeParse(composerDef.definition);
  const definition = parsed.success ? parsed.data : undefined;
  const evidenceSetIds = composerDef.recordComposerDefinitionEvidenceSets.map((r) => r.evidenceSet.id);

  const mappings: Mapping[] = definition
    ? [
        ...Object.entries(definition.entity.properties).map(([key, value]) => ({
          source: JSON.stringify(value, null, 2),
          type: t("model.common.properties"),
          target: key,
        })),
        ...Object.entries(definition.entity.metadata).map(([key, value]) => ({
          source: JSON.stringify(value, null, 2),
          type: t("model.common.metadata"),
          target: key,
        })),
      ]
    : [];

  return (
    <ComposerDetailLayout
      composerDef={composerDef}
      dipId={dipId}
      onEdit={() => {
        if (!canExecuteMutation()) return;
        setOpenEditForm(true);
      }}
      onActivate={() => {
        if (!canExecuteMutation()) return;
        onActivate({ id: composerDef.id, name: composerDef.name });
      }}
      onDeactivate={() => {
        if (!canExecuteMutation()) return;
        onDeactivate({ id: composerDef.id, name: composerDef.name });
      }}
      rawDefinition={definition ? null : composerDef.definition}
      definitionRows={
        definition ? (
          <>
            <MetaRow label={t("model.category.modelName")}>{definition.categoryNames.join(", ") || "-"}</MetaRow>
            <MetaRow label={t("model.entityComposer.entityIdResolver")}>
              <JsonValue value={definition.entityIdResolver} />
            </MetaRow>
            <MetaRow label={t("model.evidenceFilter.modelName")}>
              <JsonValue value={definition.evidenceFilter} />
            </MetaRow>
          </>
        ) : null
      }
      mappings={mappings}
      showMappingType
      readOnly={isReadOnly}
      editDialog={
        <ComposerEditDialog
          open={openEditForm && !isReadOnly}
          onOpenChange={(open) => {
            if (!open || canExecuteMutation()) setOpenEditForm(open);
          }}
          title={`${t("ui.edit")} ${t("model.entityComposer.modelName")}`}
        >
          {openEditForm && !isReadOnly ? (
            <EntityComposerEditForm
              importProjectId={dipId}
              evidenceSetIds={evidenceSetIds}
              recordComposerDefinitionId={composerDef.id}
              refetchQueriesOnEdit={[
                { query: GetRecordComposerDefinitionDetailDocument, variables: { tenantId, id: composerDef.id } },
                { query: GetRecordComposerDefinitionsDocument, variables: { tenantId, dataImportProjectId: dipId } },
              ]}
              onSubmit={() => setOpenEditForm(false)}
            />
          ) : null}
        </ComposerEditDialog>
      }
    />
  );
}

export function RelationComposerDetailPanel({ id }: { id: string }) {
  const { t } = useTranslation();
  const { isReadOnly, projectId } = useDipLifecycle();
  const { canExecuteMutation, setAuthorized } = useDipResourceMutationGuard(`relation-composer:${id}`);
  const { tenantId } = useTenant();
  const [openEditForm, setOpenEditForm] = useState(false);

  useEffect(() => {
    if (isReadOnly) setOpenEditForm(false);
  }, [isReadOnly]);

  const { data, dataState } = useSuspenseQuery(
    GetRecordComposerDefinitionDetailDocument,
    tenantId ? { variables: { tenantId, id } } : skipToken,
  );

  const { onActivate, onDeactivate } = useRelationComposerActivation({ canExecute: canExecuteMutation });

  const composerDef = tenantId && dataState === "complete" ? data?.recordComposerDefinitions[0] : undefined;
  const dipId = composerDef?.dataImportProjectRecordComposerDefinition?.dataImportProjectId;
  const isAuthorized = dipId === projectId;
  setAuthorized(isAuthorized);
  if (!composerDef || !dipId || !isAuthorized) {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  const parsed = zRelationComposerDefinition.safeParse(composerDef.definition);
  const definition = parsed.success ? parsed.data : undefined;
  const evidenceSetIds = composerDef.recordComposerDefinitionEvidenceSets.map((r) => r.evidenceSet.id);

  const mappings: Mapping[] = definition
    ? [
        ...Object.entries(definition.relation.properties).map(([key, value]) => ({
          source: JSON.stringify(value, null, 2),
          target: key,
        })),
        ...Object.entries(definition.relation.metadata).map(([key, value]) => ({
          source: JSON.stringify(value, null, 2),
          target: key,
        })),
      ]
    : [];

  return (
    <ComposerDetailLayout
      composerDef={composerDef}
      dipId={dipId}
      onEdit={() => {
        if (!canExecuteMutation()) return;
        setOpenEditForm(true);
      }}
      onActivate={() => {
        if (!canExecuteMutation()) return;
        onActivate({ id: composerDef.id, name: composerDef.name });
      }}
      onDeactivate={() => {
        if (!canExecuteMutation()) return;
        onDeactivate({ id: composerDef.id, name: composerDef.name });
      }}
      rawDefinition={definition ? null : composerDef.definition}
      definitionRows={
        definition ? (
          <>
            <MetaRow label={t("model.category.modelName")}>{definition.categoryNames.join(", ") || "-"}</MetaRow>
            <MetaRow label={t("model.relationComposer.relationIdResolver")}>
              <JsonValue value={definition.relationIdResolver} />
            </MetaRow>
            <MetaRow label={t("model.evidenceFilter.modelName")}>
              <JsonValue value={definition.evidenceFilter} />
            </MetaRow>
            <MetaRow label={t("model.relationComposer.sourceResolver")}>
              <JsonValue value={definition.sourceResolver} />
            </MetaRow>
            <MetaRow label={t("model.relationComposer.targetResolver")}>
              <JsonValue value={definition.targetResolver} />
            </MetaRow>
          </>
        ) : null
      }
      mappings={mappings}
      showMappingType={false}
      readOnly={isReadOnly}
      editDialog={
        <ComposerEditDialog
          open={openEditForm && !isReadOnly}
          onOpenChange={(open) => {
            if (!open || canExecuteMutation()) setOpenEditForm(open);
          }}
          title={`${t("ui.edit")} ${t("model.relationComposer.modelName")}`}
        >
          {openEditForm && !isReadOnly ? (
            <RelationComposerEditForm
              importProjectId={dipId}
              evidenceSetIds={evidenceSetIds}
              recordComposerDefinitionId={composerDef.id}
              refetchQueriesOnEdit={[GetRecordComposerDefinitionDetailDocument]}
              onSubmit={() => setOpenEditForm(false)}
            />
          ) : null}
        </ComposerEditDialog>
      }
    />
  );
}
